// Toast — transient notifications. ToastProvider owns a stack and exposes
// useToast() → { showToast }. Mount <ToastProvider> once near the app root;
// any component calls showToast({ message, tone }). Auto-dismisses; click to
// dismiss early. Token-styled (no MUI Snackbar needed for a simple stack).
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { cx } from '../../../utils/cx';
import { CloseIcon } from '../../icons/Icons';
import './toast.css';

const ToastContext = createContext(null);

let idSeq = 0;

export function ToastProvider({ children, duration = 4000 }) {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
  }, []);

  const showToast = useCallback(
    ({ message, tone = 'info', duration: d } = {}) => {
      const id = ++idSeq;
      setToasts((list) => [...list, { id, message, tone }]);
      timers.current[id] = setTimeout(() => dismiss(id), d || duration);
      return id;
    },
    [dismiss, duration],
  );

  const value = useMemo(() => ({ showToast, dismiss }), [showToast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-stack" role="region" aria-label={t('reusable.toast.region')}>
        {toasts.map((item) => (
          <div key={item.id} className={cx('toast', `toast--${item.tone}`)} role="status">
            <span className="toast__msg">{item.message}</span>
            <button type="button" className="toast__close" onClick={() => dismiss(item.id)} aria-label={t('reusable.toast.dismiss')}>
              <CloseIcon width={14} height={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

ToastProvider.propTypes = {
  children: PropTypes.node,
  duration: PropTypes.number,
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
};

export default ToastProvider;
